import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  CheckCircle2,
  Cog,
  Landmark,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Rocket,
  Sprout,
  Store,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { SlashMark } from './AmdenMark';
import { CONTACT } from '@/data/site';
import { submitEnquiry } from '@/services/enquiryService';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 1, label: 'Business type' },
  { id: 2, label: 'Stage' },
  { id: 3, label: 'Support' },
];

const BUSINESS_TYPES = [
  { id: 'sme', label: 'SME', description: 'A trading business looking to tighten how it runs.', icon: Store, accent: '#0B1729' },
  { id: 'established', label: 'Established Business', description: 'A long-standing organisation ready to modernise.', icon: Landmark, accent: '#FFB800' },
  { id: 'startup', label: 'Startup', description: 'An early-stage venture building solid foundations.', icon: Rocket, accent: '#029E88' },
];

const BUSINESS_STAGES = [
  { id: 'starting-out', label: 'Starting Out', description: 'Getting set up and finding your feet.', icon: Sprout, accent: '#D6318B' },
  { id: 'growing', label: 'Growing', description: 'Demand is rising and systems need to keep up.', icon: TrendingUp, accent: '#24406B' },
  { id: 'scaling', label: 'Scaling', description: 'Building structure for serious, sustained growth.', icon: BarChart3, accent: '#4225C4' },
];

const SUPPORT_OPTIONS = [
  { id: 'operations', label: 'Operations', description: 'Improve systems, accountability and day-to-day performance.', icon: Cog, accent: '#FFB800' },
  { id: 'people', label: 'People', description: 'Strengthen recruitment, HR, structure and team development.', icon: Users, accent: '#D6318B' },
  { id: 'growth', label: 'Growth', description: 'Build your brand, marketing, sales and commercial pipeline.', icon: Target, accent: '#029E88' },
  { id: 'launch', label: 'Launch', description: 'Turn a business idea into a structured, market-ready operation.', icon: Rocket, accent: '#4225C4' },
];

const EMPTY_FORM = {
  name: '',
  businessName: '',
  email: '',
  phone: '',
  location: '',
  message: '',
  consent: false,
  website: '',
};

const STARTING_POINTS = {
  idea: 'I have an idea',
  ready: 'I’m ready to start',
  rebuild: 'Rebuilding the foundations',
  'new-launch': 'Launching something new',
};

const EASE = [0.22, 1, 0.36, 1];

const SelectionCard = ({ option, selected, onSelect, testid }) => {
  const Icon = option.icon;
  const accent = option.accent; // every option carries a brand-palette colour, styled like the Our Services cards
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-testid={testid}
      style={{ borderTopWidth: '3px', borderTopColor: accent, ...(selected ? { borderColor: accent } : {}) }}
      className={cn(
        'group relative w-full rounded-xl border-2 bg-white p-5 text-left transition-[border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
        selected ? 'shadow-card' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
      )}
    >
      <span
        className={cn(
          'absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors',
          selected ? 'text-white' : 'border-slate-300 text-transparent'
        )}
        style={selected ? { borderColor: accent, backgroundColor: accent } : undefined}
        aria-hidden="true"
      >
        <Check className="h-3.5 w-3.5" />
      </span>
      <span
        className="flex h-11 w-11 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${accent}1A`, color: accent }}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="mt-4 block text-base font-bold text-navy-900">{option.label}</span>
      <span className="mt-1 block text-sm leading-relaxed text-slate-500">{option.description}</span>
    </button>
  );
};

const ProgressIndicator = ({ phase }) => (
  <ol className="mx-auto flex max-w-md items-center" aria-label="Progress" data-testid="finder-progress">
    {STEPS.map((step, i) => {
      const completed = phase === 'form' || phase > step.id;
      const current = phase === step.id;
      return (
        <li key={step.id} className={cn('flex items-center', i > 0 && 'flex-1')}>
          {i > 0 && (
            <span
              className={cn('mx-2 h-0.5 flex-1 rounded-full transition-colors duration-500 sm:mx-3', completed ? 'bg-gold-500' : current ? 'bg-navy-900' : 'bg-slate-200')}
              aria-hidden="true"
            />
          )}
          <span className="flex flex-col items-center gap-2">
            <span
              aria-current={current ? 'step' : undefined}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-[background-color,color,box-shadow] duration-300',
                completed && 'bg-gold-500 text-navy-950',
                current && 'bg-navy-900 text-white ring-4 ring-gold-600/30',
                !completed && !current && 'border-2 border-slate-300 text-slate-400'
              )}
            >
              {completed ? <Check className="h-4 w-4" aria-hidden="true" /> : step.id}
            </span>
            <span
              className={cn(
                'text-[11px] font-semibold uppercase tracking-wide sm:text-xs',
                current ? 'text-navy-900' : 'text-slate-400'
              )}
            >
              {step.label}
            </span>
          </span>
        </li>
      );
    })}
  </ol>
);

const Field = ({ id, label, required, error, children, hint }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-bold text-navy-900">
      {label}
      {required && (
        <span className="ml-1 text-red-600" aria-hidden="true">
          *
        </span>
      )}
      {required && <span className="sr-only">(required)</span>}
    </label>
    <div className="mt-1.5">{children}</div>
    {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    {error && (
      <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm font-medium text-red-600" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls = (hasError) =>
  cn(
    'h-11 w-full rounded-md border bg-white px-3.5 text-base text-navy-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1',
    hasError ? 'border-red-500' : 'border-slate-300 hover:border-slate-400'
  );

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = 'Please enter your name.';
  if (f.businessName.trim().length < 2) e.businessName = 'Please enter your business name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Please enter a valid email address.';
  if (f.message.trim().length < 10) e.message = 'Please tell us a little about your needs (at least 10 characters).';
  if (!f.consent) e.consent = 'Please confirm you’re happy for us to contact you about this enquiry.';
  return e;
};

export const SolutionFinder = () => {
  const [phase, setPhase] = useState(1); // 1 | 2 | 3 | 'form'
  const [answers, setAnswers] = useState({ businessType: null, businessStage: null, supportRequired: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [reference, setReference] = useState(null);
  const [startingPoint, setStartingPoint] = useState(null);
  const [sourceLabel, setSourceLabel] = useState('Homepage Solution Finder');
  const headingRef = useRef(null);
  const reduce = useReducedMotion();

  // Preselect a service interest when arriving via e.g. /?service=operations#solution-finder
  // Also carries an optional launch starting point and a traffic source label.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const service = params.get('service');
    if (service && SUPPORT_OPTIONS.some((o) => o.id === service)) {
      setAnswers((a) => (a.supportRequired ? a : { ...a, supportRequired: service }));
    }
    const sp = params.get('startingPoint');
    if (sp && STARTING_POINTS[sp]) setStartingPoint({ id: sp, label: STARTING_POINTS[sp] });
    const src = params.get('source');
    if (src) setSourceLabel(src.slice(0, 120));
  }, []);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [phase]);

  const selectAndAdvance = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => {
      setPhase((p) => (p === 1 ? 2 : p === 2 ? 3 : 'form'));
    }, reduce ? 0 : 260);
  };

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors((e) => ({ ...e, [key]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      const first = document.querySelector('[aria-invalid="true"]');
      first?.focus();
      return;
    }
    setStatus('submitting');
    const utm = {};
    new URLSearchParams(window.location.search).forEach((v, k) => {
      if (k.startsWith('utm_')) utm[k] = v;
    });
    const payload = {
      name: form.name.trim(),
      businessName: form.businessName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      location: form.location.trim() || undefined,
      message: form.message.trim(),
      businessType: answers.businessType,
      businessStage: answers.businessStage,
      supportRequired: answers.supportRequired,
      source: sourceLabel,
      consent: form.consent,
      pageUrl: window.location.href,
      startingPoint: startingPoint?.id || undefined,
      utm: Object.keys(utm).length ? utm : undefined,
      website: form.website || undefined,
    };
    try {
      const res = await submitEnquiry(payload);
      setReference(res.enquiryId || null);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  const labelFor = (options, id) => options.find((o) => o.id === id)?.label;

  const stepContent = {
    1: {
      heading: 'Tell us about your business',
      options: BUSINESS_TYPES,
      key: 'businessType',
      grid: 'sm:grid-cols-3',
      groupLabel: 'Select your business type',
    },
    2: {
      heading: 'What stage is your business at?',
      options: BUSINESS_STAGES,
      key: 'businessStage',
      grid: 'sm:grid-cols-3',
      groupLabel: 'Select the stage of your business',
    },
    3: {
      heading: 'What do you need support with?',
      options: SUPPORT_OPTIONS,
      key: 'supportRequired',
      grid: 'sm:grid-cols-2 lg:grid-cols-4',
      groupLabel: 'Select the support you need',
    },
  };

  const current = phase !== 'form' ? stepContent[phase] : null;

  return (
    <section
      id="solution-finder"
      aria-labelledby="finder-heading"
      data-testid="solution-finder-section"
      className="relative overflow-hidden bg-white py-20 sm:py-28"
    >
      <SlashMark
        className="pointer-events-none absolute -left-20 top-16 h-80 w-80 opacity-[0.04]"
        bar="#0B1729"
        dot="#FFB800"
      />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="04"
          eyebrow="Find Your Solution"
          title="Build a Better Business"
          description="Tell us about your business, and we’ll guide you to the right solution."
          testid="finder-header"
        />

        <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 shadow-card sm:p-10 lg:p-12">
          <ProgressIndicator phase={phase} />
          <p aria-live="polite" className="sr-only">
            {phase === 'form'
              ? 'Final step: your enquiry details'
              : `Step ${phase} of 3: ${current.heading}`}
          </p>

          <AnimatePresence mode="wait">
            {phase !== 'form' ? (
              <motion.div
                key={`step-${phase}`}
                initial={reduce ? false : { opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? undefined : { opacity: 0, x: -28 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="mt-10"
                data-testid={`assessment-step-${phase}`}
              >
                <h3
                  ref={headingRef}
                  tabIndex={-1}
                  className="text-center text-xl font-extrabold tracking-tight text-navy-900 focus:outline-none sm:text-2xl"
                >
                  {current.heading}
                </h3>
                <div
                  role="radiogroup"
                  aria-label={current.groupLabel}
                  className={cn('mt-8 grid gap-4', current.grid)}
                >
                  {current.options.map((option) => (
                    <SelectionCard
                      key={option.id}
                      option={option}
                      selected={answers[current.key] === option.id}
                      onSelect={() => selectAndAdvance(current.key, option.id)}
                      testid={`finder-option-${option.id}`}
                    />
                  ))}
                </div>
                {phase > 1 && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setPhase((p) => (p === 3 ? 2 : 1))}
                      data-testid="finder-back-btn"
                      className="group inline-flex items-center gap-2 rounded-md border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy-900 transition-colors hover:border-navy-900 hover:bg-navy-50"
                    >
                      <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" aria-hidden="true" />
                      Back
                    </button>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={reduce ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="mt-10"
              >
                {status === 'success' ? (
                  <div className="mx-auto max-w-xl py-6 text-center" data-testid="enquiry-success">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-100">
                      <CheckCircle2 className="h-8 w-8 text-gold-700" aria-hidden="true" />
                    </span>
                    <h3 className="mt-6 text-2xl font-extrabold tracking-tight text-navy-900">Thank you</h3>
                    <p className="mt-3 text-base leading-relaxed text-slate-600">
                      Thank you — your enquiry has been received. A member of the Amden team will be
                      in touch shortly.
                    </p>
                    {reference && (
                      <p className="mt-4 text-sm font-semibold text-slate-500">
                        Reference: <span className="font-mono text-navy-900">{reference}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-10 lg:grid-cols-[1fr_340px]">
                    <div>
                      <h3
                        ref={headingRef}
                        tabIndex={-1}
                        className="text-xl font-extrabold tracking-tight text-navy-900 focus:outline-none sm:text-2xl"
                      >
                        Tell us how to reach you
                      </h3>

                      {/* Summary of choices */}
                      <div className="mt-6 rounded-xl border border-slate-200 bg-mist p-5" data-testid="finder-summary">
                        <dl className={cn('grid gap-4', startingPoint ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3')}>
                          {[
                            { label: 'Business type', value: labelFor(BUSINESS_TYPES, answers.businessType), step: 1 },
                            { label: 'Stage', value: labelFor(BUSINESS_STAGES, answers.businessStage), step: 2 },
                            { label: 'Support required', value: labelFor(SUPPORT_OPTIONS, answers.supportRequired), step: 3 },
                            ...(startingPoint ? [{ label: 'Starting point', value: startingPoint.label, step: null }] : []),
                          ].map((row) => (
                            <div key={row.label} className="flex items-start justify-between gap-2 sm:block">
                              <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">{row.label}</dt>
                              <dd className="mt-1 flex items-center gap-2 text-sm font-bold text-navy-900">
                                {row.value}
                                {row.step && (
                                  <button
                                    type="button"
                                    onClick={() => setPhase(row.step)}
                                    aria-label={`Edit ${row.label}`}
                                    data-testid={`finder-edit-step-${row.step}`}
                                    className="inline-flex items-center gap-1 text-xs font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
                                  >
                                    <Pencil className="h-3 w-3" aria-hidden="true" />
                                    Edit
                                  </button>
                                )}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>

                      {status === 'error' && (
                        <div
                          role="alert"
                          data-testid="enquiry-error"
                          className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700"
                        >
                          Something went wrong while sending your enquiry. Your details are still
                          here — please try again, or call us on {CONTACT.phone}.
                        </div>
                      )}

                      <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" data-testid="enquiry-form">
                        <div className="grid gap-5 sm:grid-cols-2">
                          <Field id="name" label="Name" required error={errors.name}>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              autoComplete="name"
                              value={form.name}
                              onChange={(e) => setField('name', e.target.value)}
                              aria-invalid={!!errors.name}
                              aria-describedby={errors.name ? 'name-error' : undefined}
                              className={inputCls(!!errors.name)}
                              data-testid="enquiry-name-input"
                            />
                          </Field>
                          <Field id="businessName" label="Business name" required error={errors.businessName}>
                            <input
                              id="businessName"
                              name="businessName"
                              type="text"
                              autoComplete="organization"
                              value={form.businessName}
                              onChange={(e) => setField('businessName', e.target.value)}
                              aria-invalid={!!errors.businessName}
                              aria-describedby={errors.businessName ? 'businessName-error' : undefined}
                              className={inputCls(!!errors.businessName)}
                              data-testid="enquiry-business-input"
                            />
                          </Field>
                          <Field id="email" label="Email" required error={errors.email}>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              autoComplete="email"
                              value={form.email}
                              onChange={(e) => setField('email', e.target.value)}
                              aria-invalid={!!errors.email}
                              aria-describedby={errors.email ? 'email-error' : undefined}
                              className={inputCls(!!errors.email)}
                              data-testid="enquiry-email-input"
                            />
                          </Field>
                          <Field id="phone" label="Phone" error={errors.phone}>
                            <input
                              id="phone"
                              name="phone"
                              type="tel"
                              autoComplete="tel"
                              value={form.phone}
                              onChange={(e) => setField('phone', e.target.value)}
                              className={inputCls(!!errors.phone)}
                              data-testid="enquiry-phone-input"
                            />
                          </Field>
                          <div className="sm:col-span-2">
                            <Field id="location" label="Location" error={errors.location}>
                              <input
                                id="location"
                                name="location"
                                type="text"
                                autoComplete="address-level2"
                                placeholder="Town/County"
                                value={form.location}
                                onChange={(e) => setField('location', e.target.value)}
                                className={inputCls(!!errors.location)}
                                data-testid="enquiry-location-input"
                              />
                            </Field>
                          </div>
                        </div>
                        <Field id="message" label="Tell us about your needs" required error={errors.message}>
                          <textarea
                            id="message"
                            name="message"
                            rows={5}
                            value={form.message}
                            onChange={(e) => setField('message', e.target.value)}
                            aria-invalid={!!errors.message}
                            aria-describedby={errors.message ? 'message-error' : undefined}
                            className={cn(inputCls(!!errors.message), 'h-auto min-h-[120px] py-3')}
                            data-testid="enquiry-message-input"
                          />
                        </Field>

                        {/* Honeypot — spam protection hook for later */}
                        <div className="hidden" aria-hidden="true">
                          <label htmlFor="website">Website</label>
                          <input
                            id="website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={(e) => setField('website', e.target.value)}
                          />
                        </div>

                        <div>
                          <div className="flex items-start gap-3">
                            <input
                              id="consent"
                              name="consent"
                              type="checkbox"
                              checked={form.consent}
                              onChange={(e) => setField('consent', e.target.checked)}
                              aria-invalid={!!errors.consent}
                              aria-describedby={errors.consent ? 'consent-error' : undefined}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-navy-900 accent-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                              data-testid="enquiry-consent-checkbox"
                            />
                            <label htmlFor="consent" className="text-sm leading-relaxed text-slate-600">
                              I’m happy for Amden Group to contact me about this enquiry, as
                              described in the{' '}
                              <Link to="/privacy-policy" className="font-semibold text-navy-900 underline underline-offset-2 hover:text-gold-700">
                                privacy policy
                              </Link>
                              . <span className="text-red-600" aria-hidden="true">*</span>
                            </label>
                          </div>
                          {errors.consent && (
                            <p id="consent-error" role="alert" className="mt-1.5 text-sm font-medium text-red-600" data-testid="consent-error">
                              {errors.consent}
                            </p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={status === 'submitting'}
                          data-testid="enquiry-submit-btn"
                          className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                        >
                          {status === 'submitting' ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              Sending…
                            </>
                          ) : (
                            <>
                              Submit Enquiry
                              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                            </>
                          )}
                        </button>
                      </form>
                    </div>

                    {/* Contact card */}
                    <aside className="relative h-fit overflow-hidden rounded-2xl bg-navy-900 p-8" data-testid="finder-contact-card">
                      <SlashMark
                        className="pointer-events-none absolute -bottom-8 -right-8 h-40 w-40 opacity-10"
                        bar="#ffffff"
                        dot="#FFB800"
                      />
                      <h3 className="text-lg font-extrabold tracking-tight text-white">
                        Prefer to talk it through?
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-300">
                        Speak directly with the Amden team — no obligation, no jargon.
                      </p>
                      <ul className="mt-6 space-y-5 text-sm">
                        <li>
                          <a href={CONTACT.phoneHref} className="group flex items-start gap-3" data-testid="finder-contact-phone">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                            <span>
                              <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">Call us</span>
                              <span className="mt-0.5 block font-bold text-white transition-colors group-hover:text-gold-200">{CONTACT.phone}</span>
                            </span>
                          </a>
                        </li>
                        <li>
                          <a href={CONTACT.emailHref} className="group flex items-start gap-3" data-testid="finder-contact-email">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                            <span>
                              <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">Email</span>
                              <span className="mt-0.5 block font-bold text-white transition-colors group-hover:text-gold-200">{CONTACT.email}</span>
                            </span>
                          </a>
                        </li>
                        <li className="flex items-start gap-3" data-testid="finder-contact-address">
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                          <span>
                            <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">Office</span>
                            <span className="mt-0.5 block leading-relaxed text-slate-200">{CONTACT.address}</span>
                          </span>
                        </li>
                      </ul>
                      <Link
                        to="/contact#book-appointment"
                        data-testid="finder-book-consultation"
                        className="group mt-8 inline-flex items-center gap-2 rounded-md bg-gold-600 px-5 py-3 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-500"
                      >
                        <CalendarDays className="h-4 w-4" aria-hidden="true" />
                        Book a consultation
                      </Link>
                    </aside>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
